import { useLocale } from "../../hooks/useLocale";
import { msg } from "../../locales/translate";
import { DateTimeTooltip } from "src/view/conponents/DateTimeTooltip";
import { formatDisplayDate, formatDisplayTime, parseServerDate } from "src/utils/dateTime";
import { Check, FileText, X } from "lucide-react";
import styled from "styled-components";
import { GD } from "src/GD";
import { AdminButton } from "../pages/administration/AdminControls";
import { Card, Notice, Pill, Section, SkeletonRows } from "../providers/providersUi";

const STEPS = [
	{ status: "created", get title() { return msg("fillTheForm"); }, get text() { return msg("theApplicationIsCreatedAndItsDetailsAreBeingFilledIn"); } },
	{ status: "submitted", get title() { return msg("submitForReview"); }, get text() { return msg("theFormIsCompleteAndWaitsToBeSentToReview"); } },
	{ status: "aml_review", get title() { return msg("amlReview_ad1793b5"); }, get text() { return msg("complianceChecksTheWebsiteItsBusinessAndDocuments"); } },
	{ status: "board_review", get title() { return msg("boardReview"); }, get text() { return msg("theBoardTakesTheFinalDecisionOnTheWebsite"); } },
	{ status: "active", get title() { return msg("activated"); }, get text() { return msg("theWebsiteIsApprovedAndCanProcessPayments"); } },
] as const;

/** What happens next while the application stays at a step. */
const NEXT: Record<string, string> = {
	get created() { return msg("theClientIsFillingInTheApplicationForm"); },
	get submitted() { return msg("readyForReviewSendItWithSendToReviewInTheApplicationSection"); },
	get aml_review() { return msg("complianceIsCheckingTheWebsite"); },
	get board_review() { return msg("waitingForTheBoardSDecision"); },
};

type TStatusEvent = { status: string; date: Date; reason?: string };

const parsePayload = (value: unknown): Record<string, unknown> => {
	if (value && typeof value === "object" && !Array.isArray(value)) return value as Record<string, unknown>;
	if (typeof value !== "string") return {};
	try {
		const parsed = JSON.parse(value);
		return parsed && typeof parsed === "object" && !Array.isArray(parsed) ? parsed : {};
	} catch {
		return {};
	}
};

const formatMoment = (date?: Date) =>
	date ? `${formatDisplayDate(date, { locale: "en-US", format: { month: "short", day: "2-digit", year: "numeric" }, withZone: false })}, ${formatDisplayTime(date, { format: { hour: "2-digit", minute: "2-digit" } })}` : undefined;

const duration = (from: Date) => {
	const days = Math.floor((Date.now() - from.getTime()) / 86_400_000);
	if (days <= 0) return msg("sinceToday");
	return days === 1 ? "for 1 day" : msg("templateForDays_d30c114e", { v0: days });
};

const Progress = styled.div`
	font: 13px/1.5 "Euclid Circular B", sans-serif;
	color: #344054;
	.summary {
		display: flex;
		align-items: flex-start;
		gap: 16px;
		padding: 18px 20px;
		border-bottom: 1px solid #eaecf0;
	}
	.summary-text {
		flex: 1;
		min-width: 0;
	}
	.eyebrow {
		color: #667085;
		font-size: 12px;
	}
	.stage {
		margin-top: 2px;
		color: #182230;
		font-size: 18px;
		font-weight: 600;
		line-height: 1.35;
	}
	.since {
		margin-top: 2px;
		color: #667085;
		font-size: 12px;
	}
	.bar {
		display: grid;
		grid-template-columns: repeat(5, 1fr);
		gap: 4px;
		margin-top: 12px;
	}
	.bar span {
		height: 6px;
		border-radius: 3px;
		background: #eaecf0;
	}
	.bar span.done {
		background: #17b26a;
	}
	.bar span.current {
		background: #2e90fa;
	}
	.bar span.stopped {
		background: #f04438;
	}
	ol {
		margin: 0;
		padding: 8px 20px 12px;
		list-style: none;
	}
	li {
		position: relative;
		display: flex;
		gap: 14px;
		padding: 10px 0;
	}
	/* The line between markers fills in up to the step the application reached. */
	li:not(:last-child)::before {
		content: "";
		position: absolute;
		top: 40px;
		bottom: -6px;
		left: 13px;
		width: 2px;
		background: #eaecf0;
	}
	li.done:not(:last-child)::before {
		background: #abefc6;
	}
	.marker {
		display: grid;
		place-items: center;
		flex: 0 0 28px;
		width: 28px;
		height: 28px;
		border: 2px solid #d0d5dd;
		border-radius: 50%;
		background: #fff;
		color: #98a2b3;
		font-size: 12px;
		font-weight: 600;
	}
	li.done .marker {
		border-color: #17b26a;
		background: #17b26a;
		color: #fff;
	}
	li.stopped .marker {
		border-color: #f04438;
		background: #f04438;
		color: #fff;
	}
	li.current .marker {
		border-color: #2e90fa;
		color: #175cd3;
		box-shadow: 0 0 0 4px #d1e9ff;
	}
	.step {
		flex: 1;
		min-width: 0;
		padding-top: 3px;
	}
	.step-head {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 4px 10px;
	}
	.step-title {
		color: #182230;
		font-weight: 600;
	}
	li.upcoming .step-title {
		color: #667085;
		font-weight: 500;
	}
	.step-date {
		margin-left: auto;
		color: #98a2b3;
		font-size: 12px;
		white-space: nowrap;
	}
	.step-text {
		margin-top: 2px;
		color: #667085;
		font-size: 12px;
	}
	.next {
		display: flex;
		align-items: center;
		flex-wrap: wrap;
		gap: 8px 12px;
		margin-top: 8px;
		padding: 8px 12px;
		border: 1px solid #b2ddff;
		border-radius: 8px;
		background: #eff8ff;
		color: #175cd3;
		font-size: 12px;
	}
`;

/**
 * The website application as five steps. A step is done once the website moved past it (all of them when it is
 * active); its date is the first time the website entered that status. A rejected or blocked website shows the
 * steps it reached.
 */
export const ApplicationProgress = ({ merchant, onOpenApplication }: { merchant: IClientGroup; onOpenApplication?: () => void }) => {
	useLocale();
	const [history] = GD.REQ_API.useRequest(undefined, {
		method: "events/list",
		data: { target_id: merchant.id, target_type: "merchant", action: "merchant_status_change" },
	});

	const events: TStatusEvent[] = [];
	if (history && !history.error && Array.isArray(history.data)) {
		for (const item of history.data) {
			const payload = parsePayload(item.data);
			const status = typeof payload.new_status === "string" ? payload.new_status : undefined;
			const date = parseServerDate(item.created_at) ?? new Date(NaN);
			if (!status || Number.isNaN(date.getTime())) continue;
			events.push({ status, date, reason: typeof payload.reason === "string" && payload.reason ? payload.reason : undefined });
		}
	}
	const firstEntered = (status: string) => events.filter(event => event.status === status).sort((a, b) => a.date.getTime() - b.date.getTime())[0];
	const lastEntered = (status: string) => events.filter(event => event.status === status).sort((a, b) => b.date.getTime() - a.date.getTime())[0];

	const currentIndex = STEPS.findIndex(step => step.status === merchant.status);
	const active = merchant.status === "active";
	const outOfFlow = currentIndex === -1;
	// Out of the flow (rejected, blocked…), the last step the website reached is where it stopped.
	const lastReached = STEPS.reduce((last, step, index) => (firstEntered(step.status) ? index : last), -1);
	const stateOf = (index: number): "done" | "current" | "upcoming" | "stopped" => {
		if (active) return "done";
		if (outOfFlow) return index < lastReached ? "done" : index === lastReached ? "stopped" : "upcoming";
		if (index < currentIndex) return "done";
		return index === currentIndex ? "current" : "upcoming";
	};
	const doneCount = STEPS.filter((_, index) => stateOf(index) === "done").length;
	const statusEvent = lastEntered(merchant.status);
	const current = outOfFlow ? undefined : STEPS[currentIndex];

	return (
		<>
			<Section title={msg("applicationProgress")} />
			{outOfFlow && (
				<Notice tone={merchant.status === "rejected" ? "error" : "warning"}>
					<strong>{msg("website_00272293")}{merchant.status}</strong>
					{statusEvent && <> · <DateTimeTooltip value={statusEvent.date}>{formatMoment(statusEvent.date)}</DateTimeTooltip></>}
					{statusEvent?.reason && <div style={{ marginTop: 4 }}>{msg("reason")}{statusEvent.reason}</div>}
				</Notice>
			)}
			<Card>
				{!history ? (
					<SkeletonRows rows={3} />
				) : (
					<Progress>
						<div className="summary">
							<div className="summary-text">
								<div className="eyebrow">{active ? msg("completed") : outOfFlow ? msg("stopped") : msg("templateStepOf_9f765572", { v0: currentIndex + 1, v1: STEPS.length })}</div>
								<div className="stage">{active ? msg("websiteIsActive") : current ? current.title : msg("templateApplication_fe9797b4", { v0: merchant.status })}</div>
								{statusEvent && (
									<div className="since">
										{active ? msg("active") : outOfFlow ? merchant.status.charAt(0).toUpperCase() + merchant.status.slice(1) : msg("atThisStep")} {duration(statusEvent.date)}{msg("since")}{" "}
										<DateTimeTooltip value={statusEvent.date}>{formatMoment(statusEvent.date)}</DateTimeTooltip>
									</div>
								)}
								<div className="bar" aria-label={msg("templateOfStepsDone_18220fb0", { v0: doneCount, v1: STEPS.length })}>
									{STEPS.map((step, index) => (
										<span key={step.status} className={stateOf(index) === "upcoming" ? undefined : stateOf(index)} />
									))}
								</div>
							</div>
							<Pill tone={active ? "positive" : outOfFlow ? (merchant.status === "rejected" ? "negative" : "warning") : "info"} dot>
								{active ? msg("active") : outOfFlow ? merchant.status.charAt(0).toUpperCase() + merchant.status.slice(1) : msg("inProgress_c1f88e9d")}
							</Pill>
						</div>
						<ol>
							{STEPS.map((step, index) => {
								const state = stateOf(index);
								const date = firstEntered(step.status)?.date;
								return (
									<li key={step.status} className={state}>
										<span className="marker" aria-hidden="true">
											{state === "done" ? <Check size={15} strokeWidth={3} /> : state === "stopped" ? <X size={15} strokeWidth={3} /> : index + 1}
										</span>
										<div className="step">
											<div className="step-head">
												<span className="step-title">{step.title}</span>
												{state === "current" && (
													<Pill tone="info" dot>{msg("currentStep")}</Pill>
												)}
												{state === "stopped" && (
													<Pill tone="negative" dot>{msg("stoppedHere")}</Pill>
												)}
												{date && <span className="step-date"><DateTimeTooltip value={date}>{formatMoment(date)}</DateTimeTooltip></span>}
											</div>
											<div className="step-text">{step.text}</div>
											{state === "current" && NEXT[step.status] && (
												<div className="next">
													<span>{NEXT[step.status]}</span>
													{step.status === "submitted" && onOpenApplication && (
														<AdminButton variant="secondarySmall" size="small" onClick={onOpenApplication}>
															<FileText size={14} />{msg("openApplication")}</AdminButton>
													)}
												</div>
											)}
										</div>
									</li>
								);
							})}
						</ol>
					</Progress>
				)}
			</Card>
		</>
	);
};
